import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { RETURN_REASONS, type ReturnReason } from '@wodi/domain';
import type { ReturnableView, ReturnView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  RadioGroup,
  Select,
  Tag,
  TextArea,
  TextField,
  type TagState,
} from '@wodi/ui';
import { useState } from 'react';

import {
  decideReturn,
  findReturnable,
  pharmacyKeys,
  requestReturn,
  returnsQuery,
} from '../api/pharmacy.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { screen } from '../layout/screen.tsx';
import { WithPharmacyStore } from '../pharmacy/common.tsx';
import styles from '../pharmacy/pharmacy.module.css';
import { errorText, newRequestKey } from '../stock/common.tsx';
import { formatQuantity, readQuantity } from '../stock/format.ts';
import { toasts } from '../toasts.ts';

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  unused: 'Not used',
  wrong_item_dispensed: 'Wrong item given',
  adverse_reaction: 'Reaction to the medicine',
  prescription_changed: 'Prescription changed',
  other: 'Other',
};

const STATUS_WORDS: Record<ReturnView['status'], { text: string; tag: TagState }> = {
  requested: { text: 'Waiting for approval', tag: 'pending' },
  approved: { text: 'Approved', tag: 'done' },
  rejected: { text: 'Rejected', tag: 'failed' },
  withdrawn: { text: 'Withdrawn', tag: 'neutral' },
};

/**
 * Returns to the pharmacy (dispensing plan §8.3): take medicines back from a dispense or a
 * sale, and approve or reject what someone else asked for. Nothing goes back on the shelf
 * until a second person approves. Usable on a phone.
 */
export function PharmacyReturnsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  return (
    <WithPharmacyStore title="Returns">
      {() => (
        <>
          {session !== null && can(session, 'pharmacy.return.request') ? <TakeBack /> : null}
          <ReturnList />
        </>
      )}
    </WithPharmacyStore>
  );
}

interface LineDraft {
  quantityText: string;
  condition: 'restockable' | 'not_restockable';
}

function TakeBack() {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [sourceType, setSourceType] = useState<'sale' | 'dispense'>('sale');
  const [number, setNumber] = useState('');
  const [found, setFound] = useState<ReturnableView | null>(null);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [reason, setReason] = useState<ReturnReason>('unused');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [requestKey, setRequestKey] = useState(newRequestKey);

  const find = useMutation({
    mutationFn: () => findReturnable(sourceType, number.trim()),
    onSuccess: (view) => {
      setFound(view);
      setDrafts(
        Object.fromEntries(
          view.lines.map((l) => [
            `${l.sourceLineId}|${l.batchId}`,
            // "Cannot be reused" is the default (plan §18 C5).
            { quantityText: '', condition: 'not_restockable' as const },
          ]),
        ),
      );
    },
  });

  const chosen = found
    ? found.lines.flatMap((l) => {
        const d = drafts[`${l.sourceLineId}|${l.batchId}`];
        const quantity = d === undefined ? null : readQuantity(d.quantityText);
        return quantity === null || quantity <= 0 || d === undefined
          ? []
          : [
              {
                line: l,
                quantity,
                condition: l.mustNotRestock ? ('not_restockable' as const) : d.condition,
              },
            ];
      })
    : [];
  const tooMany = chosen.some((c) => c.quantity > c.line.returnable);
  const valid = chosen.length > 0 && !tooMany && (reason !== 'other' || note.trim() !== '');

  const submit = useMutation({
    mutationFn: () =>
      requestReturn({
        clientRequestId: requestKey,
        sourceType: found?.sourceType ?? sourceType,
        sourceId: found?.sourceId ?? '',
        reasonCode: reason,
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        lines: chosen.map((c) => ({
          sourceLineId: c.line.sourceLineId,
          batchId: c.line.batchId,
          quantity: c.quantity,
          condition: c.condition,
        })),
      }),
    onSuccess: async (ret) => {
      await queryClient.invalidateQueries({ queryKey: pharmacyKeys.all });
      toasts.show({ title: 'Return requested', description: ret.returnNumber });
      setFound(null);
      setNumber('');
      setNote('');
      setTried(false);
      setRequestKey(newRequestKey());
    },
  });

  return (
    <section className={`${styles.section} ${screen.panel}`} aria-label="Take back medicines">
      <h2 className={styles.sectionHeading}>Take back medicines</h2>
      <form
        className={screen.filters}
        onSubmit={(e) => {
          e.preventDefault();
          if (number.trim() !== '') find.mutate();
        }}
      >
        <Select
          label="From"
          options={[
            { id: 'sale', label: 'A counter sale' },
            { id: 'dispense', label: 'A dispense' },
          ]}
          value={sourceType}
          onChange={(v) => {
            setSourceType(v as 'sale' | 'dispense');
          }}
        />
        <TextField
          label={sourceType === 'sale' ? 'Sale number' : 'Dispense number'}
          description="As printed on the receipt or label."
          value={number}
          onChange={setNumber}
        />
        <div className={screen.filtersAction}>
          <Button
            type="submit"
            isPending={find.isPending}
            pendingLabel="Finding"
            isDisabled={!online}
          >
            Find
          </Button>
        </div>
      </form>
      {find.isError ? (
        <Notice tone="error">
          {errorText(find.error, "That couldn't be found. Check the number.")}
        </Notice>
      ) : null}
      {found === null ? null : (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            setTried(true);
            if (valid) submit.mutate();
          }}
        >
          {found.patient === null ? (
            <p className={styles.meta}>Counter sale: no patient was identified.</p>
          ) : (
            <PatientBannerLoader patientId={found.patient.id} />
          )}
          <p className={styles.meta}>{`Given ${formatInstant(found.givenAt)}`}</p>
          {found.lines.map((l) => {
            const key = `${l.sourceLineId}|${l.batchId}`;
            const d = drafts[key] ?? { quantityText: '', condition: 'not_restockable' as const };
            const quantity = readQuantity(d.quantityText);
            return (
              <fieldset key={key} className={styles.line}>
                <legend className={styles.lineHeading}>
                  <span>{l.itemName}</span>
                  <span className="wodi-id">{l.batchNumber}</span>
                </legend>
                <p className={styles.meta}>
                  {`Given ${formatQuantity(l.given, l.baseUnitDisplay)}; already back or asked for ${formatQuantity(l.alreadyReturned, l.baseUnitDisplay)}; can come back ${formatQuantity(l.returnable, l.baseUnitDisplay)}`}
                  {l.expiryDate === null ? '' : `. Expires ${formatIsoDate(l.expiryDate)}`}
                </p>
                <div className={styles.quantity}>
                  <TextField
                    label="Quantity back"
                    unit={l.baseUnitDisplay}
                    inputMode="numeric"
                    value={d.quantityText}
                    onChange={(text) => {
                      setDrafts((all) => ({ ...all, [key]: { ...d, quantityText: text } }));
                    }}
                    {...(quantity !== null && quantity > l.returnable
                      ? { errorMessage: `At most ${String(l.returnable)}.` }
                      : {})}
                  />
                </div>
                {l.mustNotRestock ? (
                  <p className={styles.meta}>
                    {l.isControlled
                      ? 'Controlled drug: it cannot go back on the shelf.'
                      : 'This batch has expired: it cannot go back on the shelf.'}
                  </p>
                ) : (
                  <RadioGroup
                    label="Condition"
                    options={[
                      { id: 'not_restockable', label: 'Cannot be reused' },
                      { id: 'restockable', label: 'Can go back on the shelf (sealed, in date)' },
                    ]}
                    value={d.condition}
                    onChange={(v) => {
                      setDrafts((all) => ({
                        ...all,
                        [key]: { ...d, condition: v as LineDraft['condition'] },
                      }));
                    }}
                  />
                )}
              </fieldset>
            );
          })}
          <Select
            label="Reason"
            options={RETURN_REASONS.map((r) => ({ id: r, label: RETURN_REASON_LABELS[r] }))}
            value={reason}
            onChange={(v) => {
              setReason(v as ReturnReason);
            }}
          />
          <TextArea
            label={reason === 'other' ? 'Why' : 'Note (optional)'}
            value={note}
            onChange={setNote}
            {...(tried && reason === 'other' && note.trim() === ''
              ? { errorMessage: 'Say why the medicines came back.' }
              : {})}
          />
          {tried && chosen.length === 0 ? (
            <Notice tone="error">Enter how many of at least one medicine came back.</Notice>
          ) : null}
          {submit.isError ? (
            <Notice tone="error">{errorText(submit.error, "The return wasn't recorded.")}</Notice>
          ) : null}
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isPending={submit.isPending}
              pendingLabel="Asking"
              isDisabled={!online}
            >
              Ask for approval
            </Button>
            <Button
              variant="quiet"
              onPress={() => {
                setFound(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

function ReturnList() {
  const [show, setShow] = useState<'requested' | 'all'>('requested');
  const returns = useQuery({ ...returnsQuery(show), refetchInterval: 20_000 });
  return (
    <section className={`${styles.section} ${screen.panel}`} aria-label="Returns">
      <div className={styles.headingRow}>
        <h2 className={styles.sectionHeading}>
          {show === 'requested' ? 'Waiting for approval' : 'All returns'}
        </h2>
        <Button
          dense
          variant="quiet"
          onPress={() => {
            setShow(show === 'requested' ? 'all' : 'requested');
          }}
        >
          {show === 'requested' ? 'Show all returns' : 'Show only those waiting'}
        </Button>
      </div>
      {returns.isPending ? <LoadingBlock label="Loading returns" rows={3} /> : null}
      {returns.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void returns.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(returns.error, "Returns couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {returns.isSuccess && returns.data.length === 0 ? (
        <EmptyState>
          {show === 'requested' ? 'No returns are waiting for approval.' : 'No returns yet.'}
        </EmptyState>
      ) : null}
      {returns.isSuccess ? returns.data.map((r) => <ReturnCard key={r.id} ret={r} />) : null}
    </section>
  );
}

function ReturnCard({ ret }: { ret: ReturnView }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const decide = useMutation({
    mutationFn: (action: 'approve' | 'reject' | 'withdraw') =>
      decideReturn(ret.id, action, action === 'reject' ? note.trim() : undefined),
    onSuccess: async (after) => {
      await queryClient.invalidateQueries({ queryKey: pharmacyKeys.all });
      toasts.show({
        title:
          after.status === 'approved'
            ? 'Return approved'
            : after.status === 'rejected'
              ? 'Return rejected'
              : 'Return withdrawn',
        description: after.returnNumber,
      });
      setRejecting(false);
    },
  });
  const status = STATUS_WORDS[ret.status];
  return (
    <article className={styles.line} aria-label={`Return ${ret.returnNumber}`}>
      <div className={styles.lineHeading}>
        <span>
          <span className="wodi-id">{ret.returnNumber}</span>
          <span>{` from ${ret.sourceType === 'sale' ? 'sale' : 'dispense'} `}</span>
          <span className="wodi-id">{ret.sourceNumber}</span>
        </span>
        <Tag state={status.tag}>{status.text}</Tag>
      </div>
      <p className={styles.meta}>
        {`${RETURN_REASON_LABELS[ret.reasonCode]}. Asked by ${ret.requestedBy.name}, ${formatInstant(ret.requestedAt)}`}
        {ret.decidedBy === null ? '' : `. Decided by ${ret.decidedBy.name}`}
      </p>
      <ul>
        {ret.lines.map((l) => (
          <li key={l.lineNo}>
            {`${l.itemName}, batch `}
            <span className="wodi-id">{l.batchNumber}</span>
            {`: ${formatQuantity(l.quantity, l.baseUnitDisplay)}, ${
              l.condition === 'restockable'
                ? l.restocked
                  ? 'back on the shelf'
                  : 'goes back on the shelf when approved'
                : 'cannot be reused'
            }`}
          </li>
        ))}
      </ul>
      {ret.note === null ? null : <p className={styles.meta}>{`Note: ${ret.note}`}</p>}
      {ret.decisionNote === null ? null : (
        <p className={styles.meta}>{`Decision: ${ret.decisionNote}`}</p>
      )}
      {ret.status === 'requested' ? <p>{ret.billEffect}</p> : null}
      {decide.isError ? (
        <Notice tone="error">{errorText(decide.error, "That didn't work. Try again.")}</Notice>
      ) : null}
      <div className={styles.actions}>
        {ret.canDecide ? (
          <>
            <Button
              variant="primary"
              onPress={() => {
                decide.mutate('approve');
              }}
              isPending={decide.isPending}
              pendingLabel="Approving"
              isDisabled={!online}
            >
              Approve
            </Button>
            <Button
              onPress={() => {
                setRejecting(true);
              }}
              isDisabled={!online}
            >
              Reject
            </Button>
          </>
        ) : null}
        {ret.canWithdraw ? (
          <Button
            variant="quiet"
            onPress={() => {
              decide.mutate('withdraw');
            }}
            isDisabled={!online}
          >
            Withdraw
          </Button>
        ) : null}
        {ret.status === 'requested' && !ret.canDecide ? (
          <span className={styles.meta}>Someone with approval rights decides this.</span>
        ) : null}
      </div>
      {rejecting ? (
        <Dialog
          title="Reject the return"
          isOpen
          onOpenChange={(open) => {
            if (!open) setRejecting(false);
          }}
        >
          <TextArea label="Why" value={note} onChange={setNote} autoFocus />
          <DialogActions>
            <Button
              variant="quiet"
              onPress={() => {
                setRejecting(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={() => {
                if (note.trim() !== '') decide.mutate('reject');
              }}
              isPending={decide.isPending}
              pendingLabel="Rejecting"
            >
              Reject
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </article>
  );
}
